import { Info } from 'components/common/Info'
import { Price } from 'components/common/Price'
import { Separator } from 'components/common/Separator'
import { Currencies } from 'constants/currencies'
import { TradeFee } from 'hooks/useTradeFees'
import useTranslation from 'next-translate/useTranslation'
import { Artist } from 'utils/data'

type Props = {
  artwork: { name: string; artists: Artist[] }
  tradeValue: number
  tradeType?: string
  withArtworkDetails?: boolean
  bodyClassName?: string
  summaryClassName?: string
  tradeFees: TradeFee[]
  total: number
}

const reviewTypes: { [key: string]: string } = {
  sell: 'Sale',
  ask: 'Listing',
  buy: 'Order',
  bid: 'Offer',
}

export const TradeBreakdown = ({
  artwork: { name, artists },
  tradeType,
  tradeValue,
  withArtworkDetails = true,
  bodyClassName = '',
  summaryClassName = '',
  tradeFees,
  total,
}: Props) => {
  const { t } = useTranslation('review_trade_form')

  const reviewType = tradeType ? reviewTypes[tradeType] : ''
  const formattingOptions = {
    minimumFractionDigits: 2,
    from: Currencies.USER,
    to: Currencies.USER,
  } as const

  const body = (
    <div className={bodyClassName}>
      <p className='flex justify-between text-sm font-medium'>
        {reviewType.toLowerCase() === 'order' ? (
          t('subtotal')
        ) : (
          <>
            {reviewType} {t('price')}
          </>
        )}
        <Price
          data-e2e={`${tradeType}-value`}
          formattingOptions={formattingOptions}
        >
          {tradeValue}
        </Price>
      </p>
      {tradeFees.map((tradeFee, index) => (
        <p key={index} className='flex justify-between text-sm text-cool-600'>
          {tradeFee.name}
          <Price
            data-e2e={`${tradeFee.name.toLowerCase().split(' ')[0]}-value`}
            formattingOptions={formattingOptions}
          >
            {tradeFee.value}
          </Price>
        </p>
      ))}
    </div>
  )

  const totalPayoutText = (
    <div
      className={`flex items-center justify-between font-medium ${summaryClassName}`}
    >
      {tradeType === 'bid' || tradeType === 'buy'
        ? t('total')
        : t('total_payout')}
      <Price
        className='text-lg'
        data-e2e='total-value'
        formattingOptions={formattingOptions}
      >
        {tradeValue + total}
      </Price>
    </div>
  )

  if (!withArtworkDetails)
    return (
      <>
        {body}

        <Separator color='cool-300' />

        {totalPayoutText}
      </>
    )

  return (
    <>
      {
        <h5 className='mb-4 flex items-center font-body font-medium tracking-normal'>
          {t('review')} {reviewType}
        </h5>
      }

      {tradeType === 'bid' && (
        <Info className='mb-4'>{t('you_will_only_be_charged')}</Info>
      )}

      <div className='mb-5 flex flex-col space-y-3 rounded border border-cool-300 bg-cool-50 p-4'>
        <header>
          <h6 className='font-body text-lg font-medium tracking-normal'>
            {name}
          </h6>
          <p className='text-sm text-cool-600'>
            {artists.map(artist => artist.name).join(', ')}
          </p>
        </header>

        <Separator color='cool-300' />

        {body}

        <Separator color='cool-300' />

        <footer>{totalPayoutText}</footer>
      </div>
    </>
  )
}
